import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { useDebounce } from '../../hooks/useDebounce';
import {
  ALL_CLINICS,
  useAccessibleClinics,
} from '../../hooks/useAccessibleClinics';
import type {
  DatasetManifestEntry,
  FilterOp,
  ReportDefinition,
  ReportFilter,
} from '../../types/reporting';

const SEARCH_COLUMN = 'patient_name';
const SEARCH_OP: FilterOp = 'contains';

interface PatientRosterControlsProps {
  dataset: DatasetManifestEntry;
  definition: ReportDefinition;
  onChange: (definition: ReportDefinition) => void;
}

function rosterSearchFilter(filters: ReportFilter[]): ReportFilter | undefined {
  return filters.find(
    filter => filter.column === SEARCH_COLUMN && filter.op === SEARCH_OP
  );
}

export function PatientRosterControls({
  dataset,
  definition,
  onChange,
}: PatientRosterControlsProps) {
  const pipeline =
    definition.pipeline?.type === 'patient_roster'
      ? definition.pipeline
      : undefined;
  const searchValue = rosterSearchFilter(definition.filters)?.value;
  const definitionSearch = typeof searchValue === 'string' ? searchValue : '';
  const [startDraft, setStartDraft] = useState(pipeline?.report_start ?? '');
  const [endDraft, setEndDraft] = useState(pipeline?.report_end ?? '');
  const [searchDraft, setSearchDraft] = useState(definitionSearch);
  const {
    clinics: accessibleClinics,
    selectedClinicId,
    setSelectedClinicId,
    shouldShowPicker,
    clinicsLoaded,
  } = useAccessibleClinics({ persistKey: 'patientRoster' });

  // Saved-report navigation and URL loads replace the definition; keep drafts
  // aligned with that source without clearing a user's in-progress edits.
  useEffect(() => {
    setStartDraft(pipeline?.report_start ?? '');
    setEndDraft(pipeline?.report_end ?? '');
    setSearchDraft(definitionSearch);
  }, [
    pipeline?.report_start,
    pipeline?.report_end,
    pipeline?.clinic_id,
    definitionSearch,
  ]);

  const latest = useRef({ definition, onChange });
  latest.current = { definition, onChange };
  const commitSearch = useDebounce(
    useCallback((value: string) => {
      const current = latest.current;
      const remaining = current.definition.filters.filter(
        filter => !(filter.column === SEARCH_COLUMN && filter.op === SEARCH_OP)
      );
      const filters: ReportFilter[] = value.trim()
        ? [...remaining, { column: SEARCH_COLUMN, op: SEARCH_OP, value }]
        : remaining;
      current.onChange({ ...current.definition, filters });
    }, []),
    300
  );

  useEffect(() => () => commitSearch.cancel(), [commitSearch]);

  const clinicOptions = useMemo(() => {
    const scope = dataset.clinic_scope;
    if (!scope || scope.kind === 'agency') return accessibleClinics;

    if (scope.clinic_ids !== undefined) {
      const scopedClinicIds = new Set(scope.clinic_ids);
      return accessibleClinics.filter(clinic => scopedClinicIds.has(clinic.id));
    }

    const scopedClinicNames = new Set(scope.clinic_names);
    return accessibleClinics.filter(clinic =>
      scopedClinicNames.has(clinic.name)
    );
  }, [accessibleClinics, dataset.clinic_scope]);

  const updateDates = (start: string, end: string) => {
    setStartDraft(start);
    setEndDraft(end);
    if (!start || !end || end < start || !pipeline) return;
    onChange({
      ...definition,
      pipeline: { ...pipeline, report_start: start, report_end: end },
    });
  };

  const selectedClinic = pipeline?.clinic_id ?? selectedClinicId;

  useEffect(() => {
    if (
      !pipeline ||
      pipeline.clinic_id != null ||
      selectedClinicId === ALL_CLINICS ||
      !clinicOptions.some(clinic => clinic.id === selectedClinicId)
    ) {
      return;
    }
    onChange({
      ...definition,
      pipeline: { ...pipeline, clinic_id: selectedClinicId },
    });
  }, [clinicOptions, definition, onChange, pipeline, selectedClinicId]);

  return (
    <div className='space-y-3'>
      <div className='grid grid-cols-2 gap-2'>
        <label className='space-y-1 text-xs font-medium'>
          <span>Start date</span>
          <Input
            type='date'
            aria-label='Start date'
            value={startDraft}
            onChange={event => updateDates(event.target.value, endDraft)}
          />
        </label>
        <label className='space-y-1 text-xs font-medium'>
          <span>End date</span>
          <Input
            type='date'
            aria-label='End date'
            value={endDraft}
            min={startDraft || undefined}
            onChange={event => updateDates(startDraft, event.target.value)}
          />
        </label>
      </div>
      {startDraft && endDraft && endDraft < startDraft && (
        <p role='alert' className='text-xs text-destructive'>
          End date must be on or after start date.
        </p>
      )}
      {clinicsLoaded && shouldShowPicker && clinicOptions.length > 0 && (
        <label className='block space-y-1 text-xs font-medium'>
          <span>Clinic</span>
          <Select
            value={selectedClinic}
            onValueChange={value => {
              setSelectedClinicId(value);
              if (pipeline) {
                onChange({
                  ...definition,
                  pipeline: {
                    ...pipeline,
                    clinic_id: value === ALL_CLINICS ? null : value,
                  },
                });
              }
            }}
          >
            <SelectTrigger aria-label='Clinic'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CLINICS}>All clinics</SelectItem>
              {clinicOptions.map(clinic => (
                <SelectItem key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      )}
      <label className='block space-y-1 text-xs font-medium'>
        <span>Search</span>
        <Input
          type='search'
          aria-label='Search patients'
          placeholder='Search patients'
          value={searchDraft}
          onChange={event => {
            const value = event.target.value;
            setSearchDraft(value);
            commitSearch(value);
          }}
        />
      </label>
    </div>
  );
}
